import { usesPermissionSet } from './membership';
import types from 'dashboard/store/mutation-types';
import {
  conversationFramesReady,
  conversationRevocation,
} from './conversationReads';

const contentEvents = new Set([
  'message.created',
  'message.updated',
  'first.reply.created',
  'conversation.created',
  'conversation.read',
  'conversation.updated',
  'conversation.status_changed',
  'conversation.contact_changed',
  'conversation.mentioned',
  'assignee.changed',
  'team.changed',
  'conversation.typing_on',
  'conversation.typing_off',
  'notification.created',
  'notification.updated',
  'notification.deleted',
]);

const acceptsFrame = (store, data) => {
  const accountId = store.getters.getCurrentAccountId;
  if (data.account_id !== accountId) return false;
  if (!conversationFramesReady(store.state)) return false;
  const account = store.getters.getCurrentUser?.accounts?.find(
    item => Number(item.id) === Number(accountId)
  );
  if (!account) return false;
  const revision = data.permission_set_access_revision;
  if (!usesPermissionSet(account)) {
    return revision === undefined;
  }
  return (
    typeof revision === 'string' &&
    revision === account.permission_set_access_revision
  );
};

// Frames only grant list eligibility: losses arrive as their own events and are
// confirmed with the server, so an older frame cannot hide a conversation whose
// list access was just granted.
const applyListAccess = (store, data) => {
  if (data.permission_set_list_access !== true) return;
  const conversationId = data.conversation_id ?? data.conversation?.id;
  if (!conversationId) return;
  store.commit(types.UPDATE_CONVERSATION_LIST_ACCESS, {
    conversationId,
    listAccess: data.permission_set_list_access,
  });
};

// Events merged into the conversation record by their handlers.
const recordEvents = new Set([
  'conversation.created',
  'conversation.read',
  'conversation.updated',
  'conversation.status_changed',
  'conversation.contact_changed',
  'conversation.mentioned',
  'assignee.changed',
  'team.changed',
]);

// A record never takes a list denial from a frame either (see applyListAccess),
// nor a list grant once list access to its conversation was revoked.
const recordListAccess = (event, data, listRevoked) => {
  const access = data.permission_set_list_access;
  if (!recordEvents.has(event) || access === undefined) return data;
  if (access === true && !listRevoked) return data;
  const { permission_set_list_access: _access, ...record } = data;
  return record;
};

// Display id of the conversation a frame belongs to.
const frameConversationId = (event, data) =>
  event.startsWith('notification.')
    ? data.notification?.primary_actor?.id
    : (data.conversation_id ?? data.conversation?.id ?? data.id);

export const guardConversationFrames = (events, store) =>
  Object.fromEntries(
    Object.entries(events).map(([event, handler]) => {
      if (!contentEvents.has(event)) {
        return [event, handler];
      }
      return [
        event,
        data => {
          if (!acceptsFrame(store, data)) return undefined;
          // Frames composed before a conversation's access was lost: none
          // applies after losing it, and none lists it after losing the list
          // (notifications are list-scoped).
          const revocation = conversationRevocation(
            store.state,
            frameConversationId(event, data)
          );
          if (
            revocation === 'show' ||
            (revocation && event.startsWith('notification.'))
          )
            return undefined;
          const listRevoked = revocation === 'list';
          if (!listRevoked) applyListAccess(store, data);
          return handler(recordListAccess(event, data, listRevoked));
        },
      ];
    })
  );
